/**
 * Asking the browser to keep our data.
 *
 * Without persistence a browser may evict a site's storage under disk pressure,
 * and a wedding plan is exactly the kind of data someone only notices is gone
 * months later. Asking is cheap, so it happens once, after the first plan
 * exists — a request made on first page load, before anyone has anything to
 * keep, would be both premature and (in Firefox) a confusing prompt.
 */

import { getDb } from './db'

const REQUESTED_KEY = 'persistRequested'

export async function requestPersistentStorage(): Promise<void> {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage?.persist) return
    const db = await getDb()
    if (await db.get('meta', REQUESTED_KEY)) return
    if (await navigator.storage.persisted()) return
    await db.put('meta', true, REQUESTED_KEY)
    await navigator.storage.persist()
  } catch {
    /* advisory only — a refusal changes nothing about how the app works */
  }
}

export interface StorageReport {
  /** What the plans and their photos take, in bytes. */
  bytes: number
  /** Has the browser agreed not to evict this site's data? `null` where it cannot say. */
  persisted: boolean | null
  /** Room left for this site, in bytes, where the browser says. */
  free: number | null
}

/** Less room than this left, and the dashboard says so. */
export const LOW_SPACE_BYTES = 50 * 1024 * 1024

/**
 * How much the plans take and how safe they are, for the dashboard.
 *
 * `bytes` counts our own records rather than `navigator.storage.estimate()`'s
 * usage, which also counts the browser's copies of the app itself — a number
 * nobody planning a wedding could act on.
 */
export async function storageReport(): Promise<StorageReport> {
  const db = await getDb()
  const tx = db.transaction(['plans', 'assets'], 'readonly')
  const [plans, assets] = await Promise.all([
    tx.objectStore('plans').getAll(),
    tx.objectStore('assets').getAll(),
  ])
  await tx.done

  const encoder = new TextEncoder()
  let bytes = 0
  for (const plan of plans) bytes += encoder.encode(JSON.stringify(plan)).length
  for (const asset of assets) bytes += asset.blob.size

  let persisted: boolean | null = null
  let free: number | null = null
  try {
    const storage = typeof navigator === 'undefined' ? undefined : navigator.storage
    if (storage?.persisted) persisted = await storage.persisted()
    if (storage?.estimate) {
      const { usage, quota } = await storage.estimate()
      if (usage !== undefined && quota !== undefined) free = Math.max(0, quota - usage)
    }
  } catch {
    /* the browser would rather not say */
  }
  return { bytes, persisted, free }
}
